import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { resetUnionSession } from '../../../shared/browser/unionSessionBoundary.js';
import { realtimeAllowedForCurrentOrigin } from '../../../shared/api/realtimeOrigin.js';

const DEFAULT_REALTIME_API_URL = 'https://realtime.doroparty.com';
const RECONNECT_MIN_MS = 500;
const RECONNECT_MAX_MS = 10_000;
const TICKET_TIMEOUT_MS = 10_000;
const SNAPSHOT_TIMEOUT_MS = 10_000;
// The server decides how long the mutation session lives and returns it on the ticket. Reconnecting
// just before that is the only reason to drop a healthy socket; every extra ticket costs two Supabase
// calls (auth/v1/user and get_current_user_app_context), so read the value instead of guessing.
const SESSION_REFRESH_SKEW_MS = 60_000;
const SESSION_REFRESH_MIN_MS = 60_000;
const SESSION_REFRESH_FALLBACK_MS = 14 * 60_000;
const CONNECTION_RELEASE_DELAY_MS = 1_200;
const OPERATION_TIMEOUT_MS = 12_000;
// 앱에서 돌아온 자리에서 서버가 살아 있는지 물어보고 기다리는 시간. 짧게 잡아야 "조용히 멈춘"
// 상태로 머무는 시간이 짧다. 통하는 소켓이면 왕복이 이보다 훨씬 빠르다.
const LIVENESS_TIMEOUT_MS = 2_500;
const connections = new Map();

function realtimeBaseUrl() {
  const configured = String(import.meta.env?.VITE_DORORAID_REALTIME_API_URL || '').trim();
  return (configured || DEFAULT_REALTIME_API_URL).replace(/\/+$/, '');
}

function realtimeWebSocketUrl(ticket) {
  const url = new URL(realtimeBaseUrl());
  url.protocol = url.protocol === 'http:' ? 'ws:' : 'wss:';
  url.pathname = '/ws';
  url.search = '';
  url.searchParams.set('ticket', ticket);
  return url.href;
}

function normalizeSeasonNo(value) {
  const seasonNo = Number(value);
  if (!Number.isInteger(seasonNo) || seasonNo <= 0) throw new Error('invalid_season_no');
  return seasonNo;
}

function notify(connection, method, payload) {
  for (const listener of connection.listeners) {
    try { listener?.[method]?.(payload); } catch (error) { console.error('Raid realtime listener failed', error); }
  }
}

function setStatus(connection, status, extra = {}) {
  connection.status = status;
  notify(connection, 'onStatus', { status, ...extra });
}

function clearTimer(connection, key) {
  if (!connection[key]) return;
  window.clearTimeout(connection[key]);
  connection[key] = 0;
}

function closeSocket(connection, { reconnect = false } = {}) {
  clearTimer(connection, 'refreshTimer');
  clearTimer(connection, 'snapshotTimer');
  clearTimer(connection, 'livenessTimer');
  const socket = connection.socket;
  connection.socket = null;
  if (socket && socket.readyState < WebSocket.CLOSING) {
    try { socket.close(4000, reconnect ? 'Reauthenticate' : 'Released'); } catch { /* already closing */ }
  }
}

// 안 읽음 배지 전용. 소켓도 티켓도 쓰지 않고, 최근 160개를 받아 오지도 않는다 - 방이
// '내가 아닌 사람의 마지막 글이 언제인지'와 '내 읽은 지점'만 돌려준다.
export async function loadRaidControlUnread({ seasonNo } = {}) {
  if (!hasSupabaseConfig || !supabase) throw new Error('supabase_not_configured');
  const normalizedSeasonNo = normalizeSeasonNo(seasonNo);
  const sessionResult = await supabase.auth.getSession();
  const accessToken = String(sessionResult.data?.session?.access_token || '');
  if (sessionResult.error || !accessToken) throw new Error('authentication_required');
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), TICKET_TIMEOUT_MS);
  try {
    const response = await fetch(`${realtimeBaseUrl()}/v1/raid-control/unread`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ seasonNo: normalizedSeasonNo }),
      signal: controller.signal,
    });
    if (!response.ok) throw new Error('unread_lookup_failed');
    const payload = await response.json();
    return {
      latestAt: String(payload?.latestAt || ''),
      lastReadAt: String(payload?.lastReadAt || ''),
      orderMessages: Array.isArray(payload?.orderMessages) ? payload.orderMessages : [],
    };
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function issueTicket(seasonNo) {
  if (!hasSupabaseConfig || !supabase) throw new Error('supabase_not_configured');
  const sessionResult = await supabase.auth.getSession();
  const accessToken = String(sessionResult.data?.session?.access_token || '');
  if (sessionResult.error || !accessToken) throw new Error('authentication_required');

  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, TICKET_TIMEOUT_MS);
  let response;
  try {
    response = await fetch(`${realtimeBaseUrl()}/v1/realtime-ticket`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ seasonNo }),
      signal: controller.signal,
    });
  } catch (error) {
    if (timedOut) throw new Error('realtime_ticket_timeout');
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
  let payload = null;
  try { payload = await response.json(); } catch { payload = null; }
  if (!response.ok || !payload?.ticket) {
    const error = new Error(String(payload?.error || `realtime_ticket_failed_${response.status}`));
    error.status = response.status;
    throw error;
  }
  return { ticket: String(payload.ticket), sessionExpiresIn: Number(payload.sessionExpiresIn) || 0 };
}

function sessionRefreshDelay(sessionExpiresInSeconds) {
  const lifetime = Number(sessionExpiresInSeconds) * 1_000;
  if (!Number.isFinite(lifetime) || lifetime <= 0) return SESSION_REFRESH_FALLBACK_MS;
  return Math.max(SESSION_REFRESH_MIN_MS, lifetime - SESSION_REFRESH_SKEW_MS);
}

function reconnectDelay(attempt) {
  return Math.min(RECONNECT_MIN_MS * (2 ** Math.max(0, attempt - 1)), RECONNECT_MAX_MS);
}

function scheduleReconnect(connection, { immediate = false, reason = 'closed' } = {}) {
  if (connection.released || connection.listeners.size === 0 || connection.reconnectTimer) return;
  clearTimer(connection, 'refreshTimer');
  clearTimer(connection, 'snapshotTimer');
  const delay = immediate ? 0 : reconnectDelay(connection.reconnectAttempt + 1);
  connection.reconnectAttempt += 1;
  setStatus(connection, 'reconnecting', { reason, delay });
  connection.reconnectTimer = window.setTimeout(() => {
    connection.reconnectTimer = 0;
    void connect(connection);
  }, delay);
}

// The server serves this many messages in a snapshot. The live list has to hold the same window or it
// grows for as long as the tab stays open: an all-day room ends up sorting thousands of rows on every
// incoming message, which is what made the chat crawl and then fail to send.
const SNAPSHOT_MESSAGE_LIMIT = 160;

// Date.parse over new Date(...).getTime(): same number, no object per comparison. A sort over n rows
// runs this O(n log n) times, so the allocations were the cost, not the comparison.
function messageTime(message) {
  const parsed = Date.parse(message?.created_at || '');
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortMessages(messages = []) {
  return [...messages].sort((left, right) => messageTime(left) - messageTime(right));
}

// The list is already sorted and a new message is almost always the newest, so it goes in by position
// instead of re-sorting the whole window.
function insertMessage(messages, message) {
  const at = messageTime(message);
  let index = messages.length;
  while (index > 0 && messageTime(messages[index - 1]) > at) index -= 1;
  messages.splice(index, 0, message);
  if (messages.length > SNAPSHOT_MESSAGE_LIMIT) messages.splice(0, messages.length - SNAPSHOT_MESSAGE_LIMIT);
  return messages;
}

function applyEventToSnapshot(connection, payload) {
  const snapshot = connection.snapshot;
  if (!snapshot || !payload || typeof payload !== 'object') return;
  if (payload.type === 'chat.header' || payload.type === 'chat.header.ack') {
    connection.snapshot = { ...snapshot, chatHeader: payload.header };
    return;
  }

  if (payload.type === 'message.upsert' && payload.message?.id) {
    const message = payload.message;
    const messages = (Array.isArray(snapshot.messages) ? snapshot.messages : []).filter((item) => String(item?.id || '') !== String(message.id));
    connection.snapshot = {
      ...snapshot,
      messages: message.deleted_at ? messages : insertMessage(messages, message),
    };
    return;
  }

  if (payload.type === 'message.delete' && payload.messageId) {
    connection.snapshot = {
      ...snapshot,
      chatHeader: snapshot.chatHeader?.pinnedMessage?.id === payload.messageId ? { ...snapshot.chatHeader, pinnedMessage: null } : snapshot.chatHeader,
      messages: (Array.isArray(snapshot.messages) ? snapshot.messages : []).filter((item) => String(item?.id || '') !== String(payload.messageId)),
    };
    return;
  }

  if (payload.type === 'season.chat_purged') {
    connection.snapshot = { ...snapshot, messages: [], chatHeader: { ...snapshot.chatHeader, pinnedMessage: null } };
    return;
  }

  if (payload.type === 'read.ack') {
    connection.snapshot = { ...snapshot, lastReadAt: String(payload.lastReadAt || snapshot.lastReadAt || '') };
    return;
  }

  if (payload.type === 'presence' && Array.isArray(payload.users)) {
    connection.snapshot = { ...snapshot, presence: payload.users };
  }
}

function handleSocketMessage(connection, event) {
  let payload;
  try { payload = JSON.parse(String(event.data || '')); } catch { return; }
  if (!payload || typeof payload !== 'object') return;
  if (payload.type === 'membership.changed') {
    closeSocket(connection);
    resetUnionSession('server-access-change');
    return;
  }

  if (payload.type === 'snapshot') {
    clearTimer(connection, 'snapshotTimer');
    const messages = Array.isArray(payload.messages) ? payload.messages : [];
    connection.snapshot = messages.length > SNAPSHOT_MESSAGE_LIMIT
      ? { ...payload, messages: messages.slice(-SNAPSHOT_MESSAGE_LIMIT) }
      : payload;
    connection.reconnectAttempt = 0;
    setStatus(connection, 'open');
    notify(connection, 'onSnapshot', connection.snapshot);
    return;
  }

  if (payload.type === 'pong') {
    clearTimer(connection, 'livenessTimer');
    return;
  }

  applyEventToSnapshot(connection, payload);
  notify(connection, 'onEvent', payload);
  if (payload.type === 'error' && payload.code === 'session_expired') {
    closeSocket(connection, { reconnect: true });
    scheduleReconnect(connection, { immediate: true, reason: 'session_expired' });
  }
}

async function connect(connection) {
  if (connection.released || connection.listeners.size === 0 || connection.connecting) return;
  if (connection.socket && [WebSocket.OPEN, WebSocket.CONNECTING].includes(connection.socket.readyState)) return;

  connection.connecting = true;
  clearTimer(connection, 'reconnectTimer');
  setStatus(connection, connection.reconnectAttempt ? 'reconnecting' : 'connecting');

  try {
    const { ticket, sessionExpiresIn } = await issueTicket(connection.seasonNo);
    if (connection.released || connection.listeners.size === 0) return;

    const socket = new WebSocket(realtimeWebSocketUrl(ticket));
    connection.socket = socket;
    clearTimer(connection, 'snapshotTimer');
    connection.snapshotTimer = window.setTimeout(() => {
      if (connection.socket !== socket || connection.released) return;
      setStatus(connection, 'error', { reason: 'snapshot_timeout' });
      closeSocket(connection, { reconnect: true });
      scheduleReconnect(connection, { reason: 'snapshot_timeout' });
    }, SNAPSHOT_TIMEOUT_MS);

    socket.addEventListener('open', () => {
      if (connection.socket !== socket) return;
      setStatus(connection, 'authenticating');
      clearTimer(connection, 'refreshTimer');
      connection.refreshTimer = window.setTimeout(() => {
        if (connection.socket !== socket || connection.released) return;
        closeSocket(connection, { reconnect: true });
        scheduleReconnect(connection, { immediate: true, reason: 'session_refresh' });
      }, sessionRefreshDelay(sessionExpiresIn));
    });

    socket.addEventListener('message', (event) => {
      if (connection.socket === socket) handleSocketMessage(connection, event);
    });

    socket.addEventListener('close', (event) => {
      if (connection.socket !== socket) return;
      connection.socket = null;
      clearTimer(connection, 'refreshTimer');
      clearTimer(connection, 'snapshotTimer');
      if (connection.released || connection.listeners.size === 0) {
        setStatus(connection, 'closed');
        return;
      }
      scheduleReconnect(connection, {
        immediate: event.code === 4001,
        reason: event.code === 4001 ? 'session_expired' : 'closed',
      });
    });

    socket.addEventListener('error', () => {
      if (connection.socket === socket) setStatus(connection, 'error', { reason: 'websocket_error' });
    });
  } catch (error) {
    notify(connection, 'onError', error);
    setStatus(connection, 'error', { reason: error?.message || 'connect_failed' });
    scheduleReconnect(connection, { reason: 'connect_failed' });
  } finally {
    connection.connecting = false;
  }
}

function createConnection(seasonNo) {
  return {
    seasonNo,
    listeners: new Set(),
    socket: null,
    snapshot: null,
    status: 'idle',
    connecting: false,
    released: false,
    reconnectAttempt: 0,
    reconnectTimer: 0,
    refreshTimer: 0,
    snapshotTimer: 0,
    releaseTimer: 0,
    livenessTimer: 0,
  };
}

function sendPayload(connection, payload) {
  const socket = connection.socket;
  if (!socket || socket.readyState !== WebSocket.OPEN || connection.status !== 'open') return false;
  socket.send(JSON.stringify(payload));
  return true;
}

function scheduleRelease(connection) {
  if (connection.listeners.size || connection.releaseTimer) return;
  connection.releaseTimer = window.setTimeout(() => {
    connection.releaseTimer = 0;
    if (connection.listeners.size || connections.get(connection.seasonNo) !== connection) return;
    connection.released = true;
    clearTimer(connection, 'reconnectTimer');
    clearTimer(connection, 'refreshTimer');
    clearTimer(connection, 'snapshotTimer');
    clearTimer(connection, 'livenessTimer');
    closeSocket(connection);
    connections.delete(connection.seasonNo);
  }, CONNECTION_RELEASE_DELAY_MS);
}

function operationError(code, fallback = 'realtime_operation_failed') {
  const error = new Error(String(code || fallback));
  error.code = String(code || fallback);
  return error;
}

function createRequestId(prefix = 'msg') {
  const uuid = typeof crypto?.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '')
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  return `${prefix}_${uuid}`.slice(0, 120);
}

export function isRaidControlRealtimeConfigured() {
  return Boolean(hasSupabaseConfig && supabase && realtimeBaseUrl() && realtimeAllowedForCurrentOrigin());
}

export function subscribeRaidControlRealtime({
  seasonNo,
  onSnapshot,
  onEvent,
  onStatus,
  onError,
} = {}) {
  const normalizedSeasonNo = normalizeSeasonNo(seasonNo);
  if (!realtimeAllowedForCurrentOrigin()) {
    const error = operationError('realtime_origin_not_allowed');
    queueMicrotask(() => onStatus?.({ status: 'unavailable', reason: 'origin_not_allowed' }));
    queueMicrotask(() => onError?.(error));
    return {
      send() { return false; },
      markRead() { return false; },
      getSnapshot() { return null; },
      getStatus() { return 'unavailable'; },
      unsubscribe() {},
    };
  }
  let connection = connections.get(normalizedSeasonNo);
  if (!connection) {
    connection = createConnection(normalizedSeasonNo);
    connections.set(normalizedSeasonNo, connection);
  }
  if (connection.releaseTimer) {
    clearTimer(connection, 'releaseTimer');
  }

  const listener = { onSnapshot, onEvent, onStatus, onError };
  connection.listeners.add(listener);
  connection.released = false;

  if (connection.snapshot) queueMicrotask(() => listener.onSnapshot?.(connection.snapshot));
  queueMicrotask(() => listener.onStatus?.({ status: connection.status }));
  void connect(connection);

  let active = true;
  return {
    send(payload) {
      if (!active || !payload || typeof payload !== 'object') return false;
      return sendPayload(connection, payload);
    },
    markRead(at = new Date().toISOString()) {
      if (!active) return false;
      return sendPayload(connection, { type: 'read.mark', at: String(at) });
    },
    getSnapshot() {
      return connection.snapshot;
    },
    getStatus() {
      return connection.status;
    },
    unsubscribe() {
      if (!active) return;
      active = false;
      connection.listeners.delete(listener);
      scheduleRelease(connection);
    },
  };
}

// 폰에서 앱을 전환했다 돌아오면 소켓이 이미 죽었는데도 readyState 가 OPEN 으로 남아 있는 일이
// 있다 - close 이벤트가 끝내 오지 않아서, 아무도 끊긴 줄 모른 채 채팅이 조용히 멈춘다. 돌아온
// 자리에서 서버에 한 번 물어보고(ping/pong 은 워커가 이미 받는다), 답이 없으면 바로 다시 건다.
export function resyncRaidControlRealtime(seasonNo) {
  const connection = connections.get(normalizeSeasonNo(seasonNo));
  if (!connection || connection.released || !connection.listeners.size) return false;
  const reconnectNow = (reason) => {
    clearTimer(connection, 'livenessTimer');
    closeSocket(connection, { reconnect: true });
    connection.reconnectAttempt = 0;
    scheduleReconnect(connection, { immediate: true, reason });
  };
  if (connection.status !== 'open' || connection.socket?.readyState !== WebSocket.OPEN) {
    // 이미 다시 걸고 있는 중이면 그대로 둔다. 끼어들면 백오프만 처음으로 돌아간다.
    if (connection.connecting || connection.reconnectTimer) return false;
    reconnectNow('resume');
    return true;
  }
  if (connection.livenessTimer) return false;
  if (!sendPayload(connection, { type: 'ping' })) {
    reconnectNow('resume');
    return true;
  }
  connection.livenessTimer = window.setTimeout(() => {
    connection.livenessTimer = 0;
    if (connection.released || !connection.listeners.size) return;
    reconnectNow('liveness_timeout');
  }, LIVENESS_TIMEOUT_MS);
  return true;
}

export function loadRaidControlRealtimeSnapshot({ seasonNo } = {}) {
  const normalizedSeasonNo = normalizeSeasonNo(seasonNo);
  return new Promise((resolve, reject) => {
    let settled = false;
    let subscription = null;
    const timeoutId = window.setTimeout(() => finish(operationError('realtime_snapshot_timeout')), OPERATION_TIMEOUT_MS);

    function finish(error, snapshot = null) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      subscription?.unsubscribe();
      if (error) reject(error);
      else resolve(snapshot);
    }

    subscription = subscribeRaidControlRealtime({
      seasonNo: normalizedSeasonNo,
      onSnapshot: (snapshot) => finish(null, snapshot),
      onError: (error) => {
        if (!subscription?.getSnapshot()) finish(error instanceof Error ? error : operationError('realtime_snapshot_failed'));
      },
    });
  });
}

export function sendRaidControlRealtimeMessage({
  seasonNo,
  messageKind = 'text',
  stickerId = null,
  stickerIds = null,
  body = '',
  mentionedUserIds = [],
  nikkeRefs = [],
  replyToId = null,
  practice = null,
} = {}) {
  const normalizedSeasonNo = normalizeSeasonNo(seasonNo);
  const requestId = createRequestId(messageKind === 'practice_report' ? 'practice' : 'message');
  return new Promise((resolve, reject) => {
    let settled = false;
    let sent = false;
    let subscription = null;
    const timeoutId = window.setTimeout(() => finish(operationError('realtime_message_timeout')), OPERATION_TIMEOUT_MS);

    function finish(error, message = null) {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      subscription?.unsubscribe();
      if (error) reject(error);
      else resolve(message);
    }

    function trySend() {
      if (settled || sent || !subscription || subscription.getStatus() !== 'open') return;
      sent = subscription.send({
        type: 'message.send',
        requestId,
        messageKind,
        stickerId,
        stickerIds,
        body: String(body || ''),
        mentionedUserIds: Array.isArray(mentionedUserIds) ? mentionedUserIds : [],
        nikkeRefs: Array.isArray(nikkeRefs) ? nikkeRefs : [],
        replyToId: replyToId || null,
        practice: practice && typeof practice === 'object' ? practice : undefined,
      });
    }

    subscription = subscribeRaidControlRealtime({
      seasonNo: normalizedSeasonNo,
      onSnapshot: trySend,
      onStatus: ({ status }) => { if (status === 'open') trySend(); },
      onEvent: (event) => {
        if (event?.type === 'message.upsert' && event?.requestId === requestId && event?.message) {
          finish(null, event.message);
          return;
        }
        if (event?.type === 'error' && (!event.requestId || event.requestId === requestId)) {
          finish(operationError(event.code || 'realtime_message_failed'));
        }
      },
      onError: (error) => {
        if (!sent && !subscription?.getSnapshot()) finish(error instanceof Error ? error : operationError('realtime_message_failed'));
      },
    });
  });
}

export function markRaidControlRealtimeRead({ seasonNo, at = new Date().toISOString() } = {}) {
  const normalizedSeasonNo = normalizeSeasonNo(seasonNo);
  const requestedAt = String(at || new Date().toISOString());
  return new Promise((resolve, reject) => {
    let settled = false;
    let sent = false;
    let subscription = null;
    const timeoutId = window.setTimeout(() => finish(operationError('realtime_read_timeout')), OPERATION_TIMEOUT_MS);

    function finish(error, lastReadAt = '') {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      subscription?.unsubscribe();
      if (error) reject(error);
      else resolve(String(lastReadAt || requestedAt));
    }

    function trySend() {
      if (settled || sent || !subscription || subscription.getStatus() !== 'open') return;
      sent = subscription.markRead(requestedAt);
    }

    subscription = subscribeRaidControlRealtime({
      seasonNo: normalizedSeasonNo,
      onSnapshot: trySend,
      onStatus: ({ status }) => { if (status === 'open') trySend(); },
      onEvent: (event) => {
        if (sent && event?.type === 'read.ack') finish(null, event.lastReadAt);
        if (event?.type === 'error' && event.code !== 'session_expired') finish(operationError(event.code || 'realtime_read_failed'));
      },
      onError: (error) => {
        if (!sent && !subscription?.getSnapshot()) finish(error instanceof Error ? error : operationError('realtime_read_failed'));
      },
    });
  });
}

// History is an independent request; it never replaces the shared live snapshot.
export function loadRaidControlRealtimeHistory({ seasonNo, beforeAt, beforeId }) {
  const requestId = createRequestId('history');
  return new Promise((resolve, reject) => {
    let subscription, settled = false, sent = false;
    const finish = (error, page) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      subscription?.unsubscribe();
      if (error) reject(error); else resolve(page);
    };
    const timer = window.setTimeout(() => finish(operationError('history_timeout')), OPERATION_TIMEOUT_MS);
    const trySend = () => {
      if (!settled && !sent && subscription?.getStatus() === 'open') {
        sent = subscription.send({ type: 'history.load', requestId, beforeAt, beforeId });
      }
    };
    subscription = subscribeRaidControlRealtime({ seasonNo,
      onSnapshot: trySend,
      onStatus: ({ status }) => {
        if (status === 'connecting' || status === 'reconnecting') sent = false;
        if (status === 'open') trySend();
        if (status === 'unavailable') finish(operationError('history_unavailable'));
      },
      onEvent: event => {
        if (event?.type === 'error' && event.code === 'session_expired' && !event.requestId) { sent = false; return; }
        if (sent && event?.type === 'error' && event.code === 'unsupported_mutation' && !event.requestId) { finish(operationError('history_unsupported')); return; }
        if (event?.requestId !== requestId) return;
        if (event.type === 'history.page') finish(null, event);
        if (event.type === 'error') finish(operationError(event.code));
      },
    });
  });
}

export function updateRaidControlRealtimeHeader({ seasonNo, action, body, messageId }) {
  const requestId = createRequestId('chat-header');
  return new Promise((resolve, reject) => {
    let subscription, settled = false, sent = false;
    const finish = (error, page) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      subscription?.unsubscribe();
      if (error) reject(error); else resolve(page);
    };
    const timer = window.setTimeout(() => finish(operationError('chat_header_timeout')), OPERATION_TIMEOUT_MS);
    const trySend = () => {
      if (!settled && !sent && subscription?.getStatus() === 'open') {
        sent = subscription.send({ type: 'chat.header.update', requestId, action, body, messageId });
      }
    };
    subscription = subscribeRaidControlRealtime({ seasonNo,
      onSnapshot: trySend,
      onStatus: ({ status }) => {
        if (status === 'connecting' || status === 'reconnecting') sent = false;
        if (status === 'open') trySend();
        if (status === 'unavailable') finish(operationError('chat_header_unavailable'));
      },
      onEvent: event => {
        if (event?.type === 'error' && event.code === 'session_expired' && !event.requestId) { sent = false; return; }
        if (sent && event?.type === 'error' && event.code === 'unsupported_mutation' && !event.requestId) { finish(operationError('chat_header_unsupported')); return; }
        if (event?.requestId !== requestId) return;
        if (event.type === 'chat.header.ack') finish(null, event);
        if (event.type === 'error') finish(operationError(event.code));
      },
    });
  });
}
